import React, {useState, useEffect, useRef} from 'react';
import {Spin, Image, message, Select, Card, Input, Button, Cascader, Row, Col, Tag, Space} from 'antd';
import {useAuth} from '../contexts/AuthContext';
import {DndProvider, useDrag} from 'react-dnd';
import {HTML5Backend} from 'react-dnd-html5-backend';
import {useNavigate} from 'react-router-dom';
import {SettingOutlined} from '@ant-design/icons';

const {Option} = Select;

const ItemTypes = {
    COURSE: 'course',
};

const DraggableCourse = ({course, onDragEnd, onSelect, isActive, selectedCourses}) => {
    const [{isDragging}, drag] = useDrag(() => ({
        type: ItemTypes.COURSE,
        item: {courses: selectedCourses.length > 0 ? selectedCourses : [course]},
        end: (item, monitor) => {
            const dropResult = monitor.getDropResult();
            if (item && dropResult) {
                onDragEnd(item.courses, dropResult);
            }
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    }));

    const handleClick = () => {
        if (onSelect) {
            onSelect(course);
        }
    };

    return (
        <Card
            ref={drag}
            onClick={handleClick}
            size="small"
            style={{
                opacity: isDragging ? 0.5 : 1,
                cursor: 'pointer',
                width: 'calc(33.333% - 16px)',
                margin: 8,
                border: `1px solid ${isActive ? '#1677ff' : '#d9d9d9'}`,
                backgroundColor: isActive ? '#e6f4ff' : '#fff',
                boxShadow: isActive ? '0 0 0 2px rgba(22,119,255,0.15)' : 'none',
                padding: '8px',
                textAlign: 'center',
            }}
        >
            <Image
                src={course.picUrl}
                alt={course.eventTitle}
                preview={false}
                style={{width: 80, height: 80, borderRadius: '50%', margin: '0 auto', marginBottom: 8}}
                fallback="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNkZGQiLz48dGV4dCB4PSI1MCIgeT0iNTAiIGZvcnQtc2l6ZT0iMTIiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiNmZmYiPkNvdXJzZTwvdGV4dD48L3N2Zz4="
            />
            <div>{course.eventTitle}</div>
        </Card>
    );
};

const CourseSelector = ({onCoursesSelect, selectedCourseSeqs, initialSelectedCoursesStr, onCourseTypeChange, defaultCourseType, disabled = false}) => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [coursesLoaded, setCoursesLoaded] = useState(false);
    const [courseTypeOptions, setCourseTypeOptions] = useState([]);
    const [courseLabelOptions, setCourseLabelOptions] = useState([]);
    const [selectedCourseType, setSelectedCourseType] = useState(defaultCourseType || '线下课程'); // 设置默认值
    const [selectedCourseLabel, setSelectedCourseLabel] = useState();
    const [courseLoading, setCourseLoading] = useState(false);
    const [selectedCourses, setSelectedCourses] = useState([]);
    const {apiClient} = useAuth();
    const containerRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!apiClient) return;

        const fetchCourseTypeOptions = async () => {
            setCourseLoading(true);
            try {
                const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {
                    tableName: 'COURSE_TYPE',
                    colName: 'COURSE_TYPE',
                });
                if (resp.data?.code === 200) {
                    let raw = resp.data.data;
                    if (typeof raw === 'string') {
                        raw = JSON.parse(raw);
                    }
                    const options = (raw || []).map((item) => ({
                        label: item.enumName,
                        value: item.enumCode || item.enumName
                    }));
                    setCourseTypeOptions(options);
                    
                    // 检查是否有默认值传入，否则设置默认值
                    if (defaultCourseType) {
                        setSelectedCourseType(defaultCourseType);
                    } else if (!selectedCourseType && options.length) {
                        setSelectedCourseType(options[0].value);
                    }
                } else {
                    message.error(resp.data?.msg || '获取课程区分失败');
                }
            } catch (error) {
                
                message.error('获取课程区分失败');
            } finally {
                setCourseLoading(false);
            }
        };

        const fetchCourseLabelOptions = async () => {
            try {
                const resp = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {tableName: 'SPORTS_ITEM'});
                if (resp.data && resp.data.code === 200 && resp.data.data) {
                    let raw = resp.data.data;
                    if (typeof raw === 'string') {
                        try {
                            raw = JSON.parse(raw);
                        } catch (e) {
                            
                            message.error('解析运动项目配置失败');
                            return;
                        }
                    }
                    const options = (raw || []).map((p) => ({
                        label: p.enumName,
                        value: p.enumName,
                        children: (p.items || []).map((c) => ({
                            label: c.enumName,
                            value: c.enumName,
                        })),
                    }));
                    setCourseLabelOptions(options);
                } else {
                    message.error(resp.data?.msg || '获取运动项目失败');
                }
            } catch (e) {
                
                message.error('获取运动项目失败');
            }
        };

        fetchCourseTypeOptions();
        fetchCourseLabelOptions();
    }, [apiClient]);


    // 处理初始选中课程逻辑
    useEffect(() => {
        // 只有当课程数据加载完成时才处理选中逻辑
        if (!coursesLoaded || courses.length === 0) {
            return;
        }
        
        // 如果传递了selectedCourseSeqs，优先处理
        if (selectedCourseSeqs && selectedCourseSeqs.length > 0) {

            const selected = courses.filter(course => {
                const courseSeqStr = String(course.seq || '').trim();
                return selectedCourseSeqs.some(seq => {
                    const seqStr = String(seq || '').trim();
                    return courseSeqStr === seqStr || 
                           courseSeqStr === seqStr ||
                           (course.eventTitle || '').trim() === seqStr;
                });
            });
            setSelectedCourses(selected);
        } else if (initialSelectedCoursesStr) {
            // 如果传递了字符串格式的已选课程，解析并转换为seq数组
            const selectedIdentifiers = initialSelectedCoursesStr.split(';').filter(id => id.trim() !== '');
            if (selectedIdentifiers.length > 0) {
                // 直接使用原始courses数据，只去除空格进行精确匹配
                const trimmedIdentifiers = selectedIdentifiers.map(id => id.trim());
                
                // 尝试按seq匹配（精确匹配，只去除空格）
                let matchedBySeq = courses.filter(course => 
                    trimmedIdentifiers.includes(String(course.seq || '').trim())
                );

                // 尝试按纯eventTitle匹配（精确匹配，只去除空格）- 优先级最高
                let matchedByEventTitle = courses.filter(course => 
                    trimmedIdentifiers.includes((course.eventTitle || '').trim())
                );

                // 尝试按eventTitle和courseContext匹配（精确匹配，只去除空格）- 当courseContext不为空时
                let matchedByTitleContext = courses.filter(course => {
                    if (course.courseContext) {
                        return trimmedIdentifiers.includes(`${(course.eventTitle || '').trim()}_${(course.courseContext || '').trim()}`);
                    }
                    return false;
                });

                // 尝试按_key格式匹配（精确匹配，只去除空格）
                let matchedByKey = courses.filter(course => 
                    trimmedIdentifiers.includes(`${String(course.seq || '').trim()}-${(course.eventTitle || '').trim()}`)
                );

                // 合并所有匹配结果，避免重复
                let selected = [...matchedBySeq];

                // 添加按纯eventTitle匹配的结果（优先级最高）
                matchedByEventTitle.forEach(course => {
                    if (!selected.some(c => c.seq === course.seq)) {
                        selected.push(course);
                    }
                });

                // 添加按eventTitle和courseContext匹配的结果
                matchedByTitleContext.forEach(course => {
                    if (!selected.some(c => c.seq === course.seq)) {
                        selected.push(course);
                    }
                });

                // 添加按_key格式匹配的结果
                matchedByKey.forEach(course => {
                    if (!selected.some(c => c.seq === course.seq)) {
                        selected.push(course);
                    }
                });

                setSelectedCourses(selected);
            }
        } else {
            // 没有预选课程，清空选中状态
            setSelectedCourses([]);
        }
    }, [courses, coursesLoaded, selectedCourseSeqs, initialSelectedCoursesStr, selectedCourseType]);

    const fetchCourses = async () => {
        if (!apiClient || !selectedCourseType) {
            return;
        }

        setLoading(true);
        try {
            // 获取运动项目值
            let eventTitleValue = selectedCourseLabel || '';
            // 如果selectedCourseLabel是数组（来自Cascader），用/拼接完整路径
            if (Array.isArray(eventTitleValue)) {
                eventTitleValue = eventTitleValue.join('/');
            }

            const response = await apiClient.post('/sports-server-prod/taller.api.v1/course/pageList', {
                courseKbn: selectedCourseType,
                eventTitle: eventTitleValue,
                pageNo: 1,
                pageSize: 999,
            });

            if (response.data && response.data.code === 200) {
                const list = response.data.data.list || [];
                setCourses(list);
                setCoursesLoaded(true); // 标记课程数据加载完成
            } else {
                message.error('获取课程列表失败');
                setCourses([]);
                setCoursesLoaded(false);
            }
        } catch (error) {
            message.error('获取课程列表失败');
            setCourses([]);
            setCoursesLoaded(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setCoursesLoaded(false); // 开始加载前重置状态
        fetchCourses();
    }, [selectedCourseType, selectedCourseLabel]);

    const handleDragEnd = (courses, dropResult) => {
        if (onCoursesSelect) {
            onCoursesSelect(courses, dropResult);
        }
    };


    const handleCourseTypeChange = (value) => {
        setSelectedCourseType(value);
        setCoursesLoaded(false); // 重置加载状态
        // 保留选中的课程seq，当切换课程类型后重新获取课程时，仍能恢复选择状态
        if (onCourseTypeChange) {
            onCourseTypeChange(value);
        }
    };

    const handleCourseLabelChange = (value) => {
        setSelectedCourseLabel(value);
        setCoursesLoaded(false); // 重置加载状态
    };
    const handleCourseSelect = (course) => {
        const isSelected = selectedCourses.some(selectedCourse => selectedCourse.seq === course.seq);
        let updatedSelectedCourses;
        if (isSelected) {
            updatedSelectedCourses = selectedCourses.filter(selectedCourse => selectedCourse.seq !== course.seq);
        } else {
            updatedSelectedCourses = [...selectedCourses, course];
        }
        setSelectedCourses(updatedSelectedCourses);
        // 通知父组件选中的课程
        if (onCoursesSelect) {
            onCoursesSelect(updatedSelectedCourses);
        }
    };

    return (
        <div>
            <div style={{marginBottom: 16}}>
                <Row gutter={16}>
                    <Col span={12}>
                        <Select
                            placeholder="课程区分"
                            value={selectedCourseType}
                            onChange={handleCourseTypeChange}
                            style={{width: '100%'}}
                            allowClear
                            disabled={disabled}
                        >
                            {courseTypeOptions.map((opt) => (
                                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={12}>
                        <Cascader
                            placeholder="运动项目"
                            value={selectedCourseLabel}
                            onChange={handleCourseLabelChange}
                            options={courseLabelOptions.map(opt => ({
                                label: opt.label,
                                value: opt.value,
                                children: opt.children ? opt.children.map(child => ({
                                    label: child.label,
                                    value: child.value,
                                })) : undefined,
                            }))}
                            style={{width: '100%'}}
                            allowClear
                            showSearch
                            expandTrigger="hover"
                        />
                    </Col>
                </Row>
            </div>
            {/* 显示选中的课程 */}
            <div style={{marginBottom: 16, padding: '10px', border: '1px solid #e8e8e8', borderRadius: '4px', backgroundColor: '#fafafa'}}>
                <Space wrap>
                    <span style={{fontWeight: 'bold'}}>已选动作:</span>
                    {selectedCourses.length > 0 ? (
                        selectedCourses.map(course => (
                            <Tag key={course.seq} color="blue" closable onClose={() => handleCourseSelect(course)}>
                                {course.eventTitle}
                            </Tag>
                        ))
                    ) : (
                        <span style={{color: '#aaa'}}>暂无选中动作</span>
                    )}
                    {selectedCourses.length > 0 && (
                        <span style={{color: '#1890ff', fontWeight: 'bold'}}>
                            (共{selectedCourses.length}个)
                        </span>
                    )}
                </Space>
            </div>
            <DndProvider backend={HTML5Backend}>
                <div
                    style={{
                        height: 'auto',
                        minHeight: 'calc(100vh - 200px)',
                        overflow: 'visible',
                        padding: '0 16px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '4px',
                    }}
                >
                    {loading ? (
                        <Spin/>
                    ) : (
                        <div style={{display: 'flex', flexWrap: 'wrap', justifyContent: 'center'}}>
                            {courses.map(course => (
                                <DraggableCourse
                                    key={course.seq}
                                    course={course}
                                    onDragEnd={handleDragEnd}
                                    onSelect={handleCourseSelect}
                                    isActive={selectedCourses.some(selected => selected.seq === course.seq)}
                                    selectedCourses={selectedCourses}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </DndProvider>
        </div>
    );
};

export default CourseSelector;